/**
 * Loads a resource and tracks its loading and error state.
 *
 * Extracted after the fourth screen needed the same three states. Deliberately
 * not a cache: it refetches on demand, which is what a form-driven app with a
 * handful of screens actually needs (Architecture §15).
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';

export interface ApiDataState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

export function useApiData<T>(path: string | null): ApiDataState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    api
      .get<T>(path, controller.signal)
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(
          err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
        );
        setLoading(false);
      });

    return () => controller.abort();
  }, [path, nonce]);

  return { data, error, loading, reload };
}
